import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useStageMotion } from './StageMotion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Live "people connected now" counter, animated, placed under the video toggle.
const OnlineCounter = () => {
  const [online, setOnline] = useState(null);
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef();
  useEffect(() => {
    let active = true;
    const load = async () => {
      try { const r = await fetch(`${API}/stats/public`); const d = await r.json(); if (active) setOnline(d.online || 0); } catch { /* keep last */ }
    };
    load();
    const id = setInterval(load, 8000);
    return () => { active = false; clearInterval(id); };
  }, []);
  useEffect(() => {
    if (online == null) return;
    const from = fromRef.current, to = online, start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / 600);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(tick); else fromRef.current = to;
    };
    cancelAnimationFrame(rafRef.current); rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [online]);
  if (!online || online < 1) return null;
  return (
    <div data-testid="hero-online-counter" className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-[#0B0C0E]/80 px-4 py-2">
      <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
      <span className="text-xs font-mono uppercase tracking-wider text-emerald-400"><span data-testid="hero-online-value" className="tabular-nums">{display}</span> {display === 1 ? 'en directo' : 'en directo'}</span>
    </div>
  );
};

export const HeroVideo = () => {
  const video = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const { user, loading } = useAuth();
  const { enabled: motionEnabled, reduced } = useStageMotion();
  const allowed = !loading && user?.videos_enabled !== false;
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const allow = allowed && motionEnabled && !reduced && !navigator.connection?.saveData;
    setEnabled(allow);
    if (!allow) video.current?.pause();
  }, [allowed, motionEnabled, reduced]);
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) { video.current?.pause(); }
      else if (enabled) { video.current?.play().catch(() => {}); }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled]);
  useEffect(() => {
    const element = video.current;
    element.load();
    if (enabled) element.play().catch(() => setPlaying(false));
  }, [enabled]);
  const toggle = async () => {
    if (playing) { video.current?.pause(); return; }
    if (!enabled) { setEnabled(true); return; }
    try { await video.current.play(); } catch { setPlaying(false); }
  };
  return (
    <>
      <div data-testid="hero-video-container" className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img data-testid="hero-video-poster" src="/media/stagecrew-backstage-poster.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
        <video ref={video} data-testid="hero-background-video" poster="/media/stagecrew-backstage-poster.jpg" autoPlay={enabled} loop muted playsInline preload={enabled ? 'metadata' : 'none'} tabIndex={-1} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onError={() => { setFailed(true); setPlaying(false); }} className={`absolute inset-0 w-full h-full object-cover object-center ${failed ? 'hidden' : ''}`}>
          {enabled && <><source src="/media/stagecrew-backstage.webm" type="video/webm" /><source src="/media/stagecrew-backstage.mp4" type="video/mp4" /></>}
        </video>
        <div className="absolute inset-0 bg-[#0B0C0E]/[0.65]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E]/75 via-[#0B0C0E]/[0.15] to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-transparent to-transparent" />
      </div>
      <div className="absolute bottom-4 right-4 sm:right-6 z-20 flex flex-col items-end gap-2">
        {allowed && !failed && <button type="button" data-testid="hero-video-toggle" aria-label={playing ? 'Pausar vídeo de fondo' : 'Reproducir vídeo de fondo'} aria-pressed={playing} onClick={toggle} className="sc-focus min-h-11 min-w-11 inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-[#0B0C0E]/80 px-4 text-xs text-slate-200 hover:border-amber-500/60 hover:text-amber-400 transition-colors">{playing ? <Pause size={14} /> : <Play size={14} />}<span>{playing ? 'Pausar' : 'Reproducir'}</span></button>}
        <OnlineCounter />
      </div>
    </>
  );
};
